import Bugsnag from '@bugsnag/js';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  Label,
  Text,
} from '@civilgrid/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { groupBy, keys } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdVideocam } from 'react-icons/md';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { transcodeImage as apiTranscodeImage } from '~/api/misc2';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Dropdown } from '~/components2/Dropdown';
import Icon from '~/components2/Icon';
import { clickedMapCanvas } from '~/components2/Map/clickedMapCanvas';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { Notify } from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import { useBulkPhotoImportEnabled } from '~/hooks/useBulkPhotoImportEnabled';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import * as DropPhotos from '../dropPhotos';
import { FIELD_NOTE_MEDIA_FILL, FIELD_NOTE_NOTE_FILL, FIELD_NOTE_STROKE } from '../map/siteDataLegend.helpers';
import { CivilGridMobileCode } from '../potholing/common';
import { buildFieldNoteFeatures } from './buildFieldNoteFeatures';
import { generateConeImage, generateConeImageHover } from './directionCone';
import FieldNoteViewer from './FieldNoteViewer';
import { DIRECTION_CONES_LAYOUT, DIRECTION_CONES_PAINT, FIELD_NOTES_CIRCLE_EMISSIVE_PAINT } from './mapStyle';
import store, { type FieldNote, type FieldNotesExportFileType, parseFieldNoteFeature } from './store';
import { useFieldNoteDeepLink } from './useFieldNoteDeepLink';

export const FIELD_NOTES_LAYER_ID = 'field-notes';
const DIRECTION_CONES_LAYER_ID = 'direction-cones';
let hasNotifiedConeImageError = false;

function isExpectedStyleNotReadyError(e: unknown): boolean {
  const message =
    e instanceof Error
      ? e.message
      : typeof e === 'string'
        ? e
        : typeof (e as any)?.message === 'string'
          ? (e as any).message
          : '';

  if (!message) return false;

  // In mapbox-gl@2.15.0 this comes from Style#_checkLoaded():
  // `throw new Error('Style is not done loading')`
  // We expect that race here and retry via the `style.load` listener.
  return message === 'Style is not done loading';
}

function reportConeImageError(e: unknown) {
  if (isExpectedStyleNotReadyError(e)) return;

  if (!hasNotifiedConeImageError) {
    hasNotifiedConeImageError = true;
    const error = e instanceof Error ? e : new Error(`Error adding direction cone images: ${String(e)}`);
    Bugsnag.notify(error);
  }
  console.error('Error adding direction cone images', e);
}

type FieldNotesLayersProps = {
  fieldNotes: readonly FieldNote[];
  hoveredItem?: FieldNote | null;
  /** Larger pin for PDF per-note detail maps (headless export). */
  mapRenderMode?: 'default' | 'exportDetail';
  /** False on read-only surfaces (print view): draw the markers, register no click handler. */
  interactive?: boolean;
};

export function FieldNotesLayers({
  fieldNotes,
  hoveredItem,
  mapRenderMode = 'default',
  interactive = true,
}: FieldNotesLayersProps) {
  const isExportDetail = mapRenderMode === 'exportDetail';
  const { current: map } = MapGL.useMap();

  // Register direction cone images during render, before react-map-gl's
  // Layer component calls map.addLayer() (which also happens during render).
  // This ensures images exist before the symbol layer references them.
  if (map) {
    const m = map.getMap();
    try {
      if (!m.hasImage('directionCone')) {
        m.addImage('directionCone', generateConeImage());
      }
      if (!m.hasImage('directionConeHover')) {
        m.addImage('directionConeHover', generateConeImageHover());
      }
    } catch (e) {
      reportConeImageError(e);
      // Style not yet ready for image operations; the useEffect
      // style.load listener will register images once it is.
    }
  }

  // Re-register cone images after style changes (e.g. basemap switch)
  useEffect(() => {
    if (!map) return;
    const m = map.getMap();

    function addConeImages() {
      if (!m.hasImage('directionCone')) {
        m.addImage('directionCone', generateConeImage());
      }
      if (!m.hasImage('directionConeHover')) {
        m.addImage('directionConeHover', generateConeImageHover());
      }
    }

    m.on('style.load', addConeImages);
    try {
      addConeImages();
    } catch (e) {
      reportConeImageError(e);
    }
    return () => {
      m.off('style.load', addConeImages);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !interactive) return;
    map.on('click', FIELD_NOTES_LAYER_ID, handleFieldNotesLayerClick);
    map.on('click', DIRECTION_CONES_LAYER_ID, handleFieldNotesLayerClick);
    return () => {
      map.off('click', FIELD_NOTES_LAYER_ID, handleFieldNotesLayerClick);
      map.off('click', DIRECTION_CONES_LAYER_ID, handleFieldNotesLayerClick);
    };
  }, [map, interactive]);

  const mapPoints = useMemo(() => {
    return (
      <MapGL.Source
        type="geojson"
        data={{
          type: 'FeatureCollection',
          features: buildFieldNoteFeatures(fieldNotes, hoveredItem?.id),
        }}
      >
        <MapGL.Layer
          id={FIELD_NOTES_LAYER_ID}
          type="circle"
          paint={{
            // Type-driven fill: notes are white; image/video keep amber/orange.
            // Default branch falls through to amber so unknown future types
            // render a neutral pin rather than crashing the expression.
            // Fill/stroke colors are shared with the legend swatch (siteDataLegend.helpers.ts)
            // so the two can't drift.
            'circle-color': [
              'match',
              ['get', 'type'],
              'note',
              FIELD_NOTE_NOTE_FILL,
              ['image', 'video'],
              ['case', ['get', 'isHovered'], 'orange', FIELD_NOTE_MEDIA_FILL],
              FIELD_NOTE_MEDIA_FILL,
            ],
            'circle-radius': isExportDetail ? 22 : 7,
            'circle-stroke-color': [
              'match',
              ['get', 'type'],
              'note',
              ['case', ['get', 'isHovered'], 'green', FIELD_NOTE_STROKE],
              ['image', 'video'],
              ['case', ['get', 'isHovered'], 'green', FIELD_NOTE_STROKE],
              FIELD_NOTE_STROKE,
            ],
            'circle-stroke-width': isExportDetail ? 4 : ['get', 'strokeWidth'],
            ...FIELD_NOTES_CIRCLE_EMISSIVE_PAINT,
          }}
        />
        <MapGL.Layer
          id={DIRECTION_CONES_LAYER_ID}
          type="symbol"
          filter={['has', 'compassHeading']}
          layout={DIRECTION_CONES_LAYOUT}
          paint={DIRECTION_CONES_PAINT}
        />
      </MapGL.Source>
    );
  }, [fieldNotes, hoveredItem, isExportDetail]);

  return <>{mapPoints}</>;
}

export function MapChildren() {
  // This tab uses left-click to select field notes, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { fieldNotes, hoveredItem } = useSnapshot(store) as typeof store;
  return <FieldNotesLayers fieldNotes={fieldNotes} hoveredItem={hoveredItem} />;
}

function handleSelectAllFieldNotesChange(isChecked: boolean | 'indeterminate', fieldNotes: FieldNote[]) {
  if (isChecked) {
    store.clearSelectedIds();
    fieldNotes.forEach(({ id }) => {
      store.selectedIds.add(id);
    });
  } else {
    store.clearSelectedIds();
  }
}

function handleFieldNoteSelectionChange(isChecked: boolean | 'indeterminate', itemId: string) {
  if (isChecked) {
    store.selectedIds.add(itemId);
  } else {
    store.selectedIds.delete(itemId);
  }
}

function handleFieldNoteMouseEnter(item: FieldNote) {
  store.setHoveredItem(item);
}

function handleFieldNoteMouseLeave() {
  store.setHoveredItem(null);
}

function openFieldNoteDetail(item: FieldNote) {
  store.openDetailItem(item, 'grid');
}

async function handleFieldNotesLayerClick(event: MapGL.MapMouseEvent) {
  // don't send click event to map if measure tool is active
  if (measureStatus.isMeasuring === true) return;
  // A pothole marker drawn over this note owns the click.
  if (!clickedMapCanvas(event)) return;

  const { features } = event;

  if (features && features?.length > 0) {
    // TODO: should handle more than one item at the same spot
    const item = parseFieldNoteFeature(features[0].properties?.item);
    if (!item) {
      // Malformed/missing feature payload — don't let the map click handler throw.
      Bugsnag.notify(new Error('Error parsing field note feature from map click'));
      console.error('Error parsing field note feature', features[0].properties?.item);
      Notify.error({
        title: 'Field Notes',
        message: 'There was an error opening that field note, please contact support.',
      });
      return;
    }
    store.openDetailItem(item, 'map');
  }
}

function FieldNoteGridItem({
  item,
  isSelected,
  selectionModeActive,
}: {
  item: FieldNote;
  isSelected: boolean;
  selectionModeActive: boolean;
}) {
  const handleSelectionChange = useCallback(
    (isChecked: boolean | 'indeterminate') => handleFieldNoteSelectionChange(isChecked, item.id),
    [item.id],
  );
  const handleMouseEnter = useCallback(() => handleFieldNoteMouseEnter(item), [item]);
  const handleMouseLeave = useCallback(handleFieldNoteMouseLeave, []);
  const hoverHandlers = { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };

  return (
    <div
      className="group"
      style={{
        padding: 5,
        position: 'relative',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <div
        className={`absolute right-0 top-0 z-10 m-2 rounded bg-white/90 p-0.5 shadow-sm group-hover:block ${selectionModeActive ? 'block' : 'hidden'}`}
      >
        <Checkbox
          className="block"
          data-testid={`field-note-select-checkbox-${item.id}`}
          aria-label={`Select ${item.type} field note from ${item.createdAt.toLocaleString()}`}
          checked={isSelected}
          onCheckedChange={handleSelectionChange}
        />
      </div>
      {item.type === 'note' ? (
        <NoteCard item={item} {...hoverHandlers} />
      ) : (
        <Thumbnail item={item} {...hoverHandlers} />
      )}
    </div>
  );
}

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { fieldNotes, selectedIds } = useSnapshot(store) as typeof store;
  const bulkPhotoImportEnabled = useBulkPhotoImportEnabled();
  useFieldNoteDeepLink(projectId);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importViewerOpen, setImportViewerOpen] = useState(false);
  const [exportFileType, setExportFileType] = useState<FieldNotesExportFileType | null>(null);

  useEffect(() => {
    store.fetchFieldNotes(projectId);
  }, [projectId]);

  const handleImported = useCallback(() => store.fetchFieldNotes(projectId), [projectId]);

  useEffect(() => {
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectFieldNotesTabViewed, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
    });
  }, [projectId]);

  const handleSelectAllChange = useCallback(
    (isChecked: boolean | 'indeterminate') => handleSelectAllFieldNotesChange(isChecked, fieldNotes),
    [fieldNotes],
  );

  const exportDropdownItems = useMemo(
    () => [
      {
        type: 'simple' as const,
        label: 'PDF (.pdf)',
        onClick: () => setExportFileType('pdf'),
      },
      {
        type: 'simple' as const,
        label: 'ZIP (.zip)',
        onClick: () => setExportFileType('zip'),
      },
    ],
    [],
  );

  const selectionModeActive = selectedIds.size > 0;

  const importButton = bulkPhotoImportEnabled ? (
    <Button variant="secondary" data-testid="field-notes-import-photos-button" onClick={() => setImportOpen(true)}>
      Import photos
    </Button>
  ) : null;

  // Empty-state pitch for the same button, wrapper included, since neither exists without the flag.
  const importPrompt = bulkPhotoImportEnabled ? (
    <div className="mb-6 flex flex-col items-center gap-2">
      <Text className="text-sm text-grey-70">
        Have geotagged photos? Import them, or a .zip of them, and each becomes a field note.
      </Text>
      {importButton}
    </div>
  ) : null;

  // Outside-click and Esc are suppressed so an in-progress import isn't lost to a stray click. While
  // the import is reading or creating, close is blocked entirely (the X is hidden) so the dialog
  // can't unmount mid-run and abort it; it becomes closable again once the run settles.
  const importDialog = bulkPhotoImportEnabled ? (
    <Dialog
      open={importOpen}
      onOpenChange={(open) => {
        if (!open && importBusy) return;
        setImportOpen(open);
      }}
    >
      <DialogContent
        className="max-w-3xl"
        data-testid="field-notes-import-dialog"
        title="Import photos"
        preventDismiss
        dismissible={!importBusy && !importViewerOpen}
      >
        <DialogBody>
          <DropPhotos.TabDetails
            onImported={handleImported}
            onBusyChange={setImportBusy}
            onViewerOpenChange={setImportViewerOpen}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  ) : null;

  const els = useMemo(() => {
    const content: JSX.Element[] = [];
    if (fieldNotes.length > 0) {
      const dates = groupBy(
        fieldNotes,
        (note) => note.createdAt.toFormat('MMM dd, yyyy'), // make date parsing better
      );

      keys(dates).forEach((date) => {
        const items = dates[date].map((item) => (
          <FieldNoteGridItem
            key={item.id}
            item={item}
            isSelected={selectedIds.has(item.id)}
            selectionModeActive={selectionModeActive}
          />
        ));

        content.push(
          <div key={date.toString()}>
            <h5>
              <b>{date}</b>
            </h5>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              {items}
            </div>
          </div>,
        );
      });

      return content;
    } else {
      return null;
    }
  }, [fieldNotes, selectedIds, selectionModeActive]);

  // One shared return keeps importDialog at a stable position across the empty/populated switch, so
  // the first import (which fills the empty state) doesn't remount the dialog and lose its progress.
  return (
    <div className="w-full p-2">
      {fieldNotes.length === 0 ? (
        <>
          {importPrompt}
          <CivilGridMobileCode message="Use the CivilGrid mobile app to capture images and videos in the field that can be reviewed here!" />
        </>
      ) : (
        <>
          <div className="my-2 flex justify-between space-x-2">
            <Label className="cursor-pointer">
              <Checkbox
                data-testid="field-notes-select-all-checkbox"
                checked={store.selectedIds.size === fieldNotes.length}
                onCheckedChange={handleSelectAllChange}
              />
              Select all
            </Label>
            <div className="flex items-center gap-2">
              {importButton}
              <Dropdown
                side="bottom"
                align="start"
                triggerElement={<Button data-testid="field-notes-export-button">Export</Button>}
                disabled={selectedIds.size === 0}
                items={exportDropdownItems}
              />
            </div>
          </div>
          {els}
          <FieldNoteViewer />
        </>
      )}
      {importDialog}
      <SendEmailModal
        open={exportFileType !== null}
        onOpenChange={(open) => {
          if (!open) setExportFileType(null);
        }}
        title="Export field notes"
        testIdPrefix="field-notes-export"
        onSend={(ccEmails) =>
          exportFileType && store.exportFieldNotes(exportFileType, ccEmails.length > 0 ? ccEmails : undefined)
        }
      />
    </div>
  );
}

const SIZE = 150;

function NoteCard({
  item,
  onMouseEnter,
  onMouseLeave,
}: {
  item: FieldNote;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const body = item.body?.trim() ?? '';
  const handleClick = useCallback(() => openFieldNoteDetail(item), [item]);
  const styles: React.CSSProperties = {
    width: SIZE,
    height: SIZE,
  };
  return (
    <button
      type="button"
      data-testid={`field-note-note-card-${item.id}`}
      className="flex cursor-pointer flex-col rounded-md border border-grey-20 bg-grey-10 p-2 text-left"
      style={styles}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-grey-60">
        <Icon icon="edit" size={12} />
        <span>Note</span>
      </div>
      <div className="flex-1 overflow-hidden whitespace-pre-wrap break-words text-xs leading-tight text-grey-80">
        {body || <span className="italic text-grey-50">No content</span>}
      </div>
    </button>
  );
}

function Thumbnail({
  item,
  onMouseEnter,
  onMouseLeave,
}: {
  item: FieldNote;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const file = item.files?.[0];
  const path = file?.path;
  const pathKey = path?.split('?')?.[0];
  const isVideo = item.type === 'video' || file?.type?.toLowerCase()?.includes('video');
  const handleClick = useCallback(() => openFieldNoteDetail(item), [item]);

  const transcoder = useQuery({
    enabled: !!pathKey && !isVideo,
    queryKey: ['transcodeImage', { path: pathKey, width: SIZE, height: SIZE }],
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    queryFn: () =>
      apiTranscodeImage({
        url: path,
        projectId,
        width: SIZE,
        height: SIZE,
      }),
  });

  const styles: React.CSSProperties = {
    width: SIZE,
    height: SIZE,
  };

  if (transcoder.isFetching) {
    return (
      <div className="flex items-center justify-center rounded-md border bg-cover bg-center bg-repeat" style={styles}>
        <CircularProgress indeterminate />
      </div>
    );
  }

  const thumbnailPath = isVideo ? file?.thumbnailPath : transcoder.data;

  const typeLabel = isVideo ? 'Video' : 'Image';
  const caption = file?.notes?.trim();
  const dateLabel = item.createdAt.toFormat('MMM dd, yyyy');
  const ariaLabel = caption
    ? `${typeLabel}, ${caption.length > 50 ? `${caption.slice(0, 50)}…` : caption}`
    : dateLabel
      ? `${typeLabel}, ${dateLabel}`
      : `${typeLabel} attachment ${item.id}`;

  return (
    <button
      type="button"
      className="relative flex cursor-pointer items-center justify-center rounded-md border bg-cover bg-center bg-repeat p-0"
      style={{ ...styles, background: `url(${thumbnailPath})` }}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
      data-testid={`field-note-thumbnail-${item.id}`}
    >
      {isVideo && (
        <div
          className="absolute right-1.5 bottom-1.5 flex items-center justify-center rounded-full bg-black/60 p-1 text-white"
          data-testid={`field-note-video-indicator-${item.id}`}
        >
          <MdVideocam size={18} />
        </div>
      )}
    </button>
  );
}
